/**
 * useStartSession hook
 * Implements US-015; modified from original US-003 implementation.
 * Modified US-021: detects 402 INSUFFICIENT_COINS and exposes isOutOfCoins flag.
 * React Query mutation hook for starting new serverless tutoring sessions.
 * Handles navigation to session chat page on success and user-friendly error messages.
 * On 422 ACTIVE_SESSION_EXISTS / conflict family: seamlessly redirects to existing active
 * session instead of showing a generic error (AC-1).
 * BUG-20260701-230408 (RC-003): the conflict family also recognizes SESSION_ALREADY_ACTIVE,
 * the 422 code actually emitted by start_session.py's commercial one-active-per-child guard -
 * without this, a correct backend 422 fell through to a generic, unhelpful error string - 2026-07-02
 * Enforces TO-LL-026: uses refetchQueries before resolving active session id to avoid
 * DynamoDB eventual-consistency stale reads; falls back to /sessions if unresolvable.
 * Enforces TO-LL-013: navigate() targets are validated internal paths only (/session/{id}
 * or /sessions) — never server-supplied free-form URLs (open-redirect avoidance).
 * US-021: Enforces CCM-03 — the INSUFFICIENT_COINS error string is SUPPRESSED so the raw
 * backend 'Top up to start a new session' message is NEVER rendered to a student. Only the
 * error CODE is inspected; all other error codes remain unaffected (TO-LL-036).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { startServerlessSession, getSessions } from '@/services/sessions';
import type { ServerlessSessionResponse } from '@/types/session';
import type { AuthError } from '@/types/auth';
import { AUTH_ERROR_CODES } from '@/types/auth';

export interface UseStartSessionReturn {
  /** Function to start a new session with optional topic preference */
  startSession: (preferredTopic?: string) => void;
  /** True while session creation is in progress */
  isStarting: boolean;
  /** User-friendly error message if session start failed */
  error: string | null;
  /** True after successful session creation */
  isSuccess: boolean;
  /** Reset mutation state */
  reset: () => void;
  /**
   * US-021: True after backend returns 402 INSUFFICIENT_COINS (family wallet is empty).
   * Consuming pages must render the neutral "0 sessions remaining" state and HIDE the Start
   * button when this is true (CCM-03: no selling / no "ask a grown-up" — sessions-framed only).
   * The sticky ['family','outOfCoins'] React Query cache key is also set on 402 so
   * SessionsListPage and StudentDashboardPage both react without a re-attempt (CCM-03, AC-1).
   */
  isOutOfCoins: boolean;
}

/**
 * Map AuthError to user-friendly error message.
 * US-015: The conflict family (ACTIVE_SESSION_EXISTS, SESSION_ALREADY_ACTIVE, CONFLICT,
 * SESSION_LIMIT_REACHED, RATE_LIMITED) is NO LONGER surfaced as a visible error — those
 * codes trigger the seamless resume redirect in resolveAndResumeActiveSession() instead.
 * BUG-20260701-230408 (RC-003): SESSION_ALREADY_ACTIVE added to the conflict family - 2026-07-02
 * This function is only invoked for NON-conflict errors (AC-1, TO-LL-036).
 * @param error - The AuthError from API
 * @returns User-friendly error message string
 */
function getErrorMessage(error: AuthError): string {
  // 403 - Free user has used all demo sessions (US-004, AC-004.3)
  if (error.code === 'DEMO_SESSION_LIMIT') {
    return 'You have used all your available demo sessions.';
  }

  // 403 - Student's allowedMode is 'mcp-only'
  if (error.code === 'FORBIDDEN') {
    return 'You do not have permission to start sessions independently.';
  }

  // 404 - ContentLibrary is empty (NO_CONTENT_AVAILABLE)
  if (
    error.code === 'NO_CONTENT_AVAILABLE' ||
    error.message?.includes('No tutoring sessions') ||
    ('status' in error && error.status === 404)
  ) {
    return 'No tutoring sessions are available right now. Please check back later.';
  }

  // 401 - Token expired or not authenticated
  if (error.code === 'UNAUTHORIZED') {
    return 'Please log in to start a new session.';
  }

  // 409/422 - Active session already exists (safety net for race condition, US-003).
  // BUG-20260701-230408 (RC-003): SESSION_ALREADY_ACTIVE is the 422 code emitted by the
  // commercial one-active-per-child guard in start_session.py; it belongs in this branch
  // alongside ACTIVE_SESSION_EXISTS (the /decisions 409 equivalent) - 2026-07-02
  if (
    error.code === 'ACTIVE_SESSION_EXISTS' ||
    error.code === 'SESSION_ALREADY_ACTIVE' ||
    error.code === 'CONFLICT'
  ) {
    return 'You already have an active session. Please complete it before starting a new one.';
  }

  // 429 - Session limit reached (abuse guard in start_session.py, US-003)
  if (error.code === 'SESSION_LIMIT_REACHED' || error.code === 'RATE_LIMITED') {
    return 'Please complete an existing session before starting a new one.';
  }

  // Network error
  if (error.code === 'NETWORK_ERROR') {
    return 'Unable to connect to server. Please check your internet connection.';
  }

  // Generic error fallback
  return error.message || 'Something went wrong. Please try again later.';
}

/**
 * Hook to start a new serverless session with navigation on success.
 * Implements US-015; modified from US-003.
 * Provides mutation function that calls startServerlessSession, navigates to the new session
 * chat page, and transforms errors into user-friendly messages for display.
 *
 * US-015 extension: On the conflict family (ACTIVE_SESSION_EXISTS / SESSION_ALREADY_ACTIVE /
 * CONFLICT / SESSION_LIMIT_REACHED / RATE_LIMITED), instead of showing a generic error string the hook
 * invokes resolveAndResumeActiveSession() which refetches ['sessions'] (per TO-LL-026 —
 * prevents DynamoDB eventual-consistency stale reads) and navigates seamlessly to the
 * existing active session. Navigate targets are validated internal paths only (TO-LL-013).
 *
 * Non-conflict errors still flow through getErrorMessage() and are surfaced to the UI
 * unchanged (TO-LL-036: never swallow errors silently).
 *
 * @returns Object with startSession function and state flags
 *
 * @example
 * ```typescript
 * const { startSession, isStarting, error } = useStartSession();
 *
 * const handleStartSession = (topic?: string) => {
 *   startSession(topic);
 * };
 * ```
 */
export function useStartSession(): UseStartSessionReturn {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // US-015: Tracks whether the current/last error was a conflict redirect.
  // When true, the hook suppresses the error string (returns null) because the
  // conflict is handled by seamless navigation, not an error message (AC-1).
  // A plain ref avoids triggering re-renders in the onError callback.
  const isConflictRedirectRef = useRef(false);

  // US-021: Tracks whether the family wallet is empty (backend returned 402 INSUFFICIENT_COINS).
  // Uses useState (not a ref) so the UI re-renders when it flips to true.
  // Implements CCM-03: this flag drives the neutral "0 sessions remaining" state; the raw
  // backend error string is NEVER surfaced. Enforces TO-LL-036: only this specific error code
  // is handled here — all other codes continue flowing to getErrorMessage unchanged.
  const [isOutOfCoins, setIsOutOfCoins] = useState(false);

  /**
   * US-015 T1: Seamlessly redirect to the existing active session after a 422 conflict.
   * Enforces TO-LL-026: refetchQueries before reading to avoid DynamoDB eventual-consistency
   * stale data — a bare invalidateQueries may not have settled when we need to read.
   * Enforces TO-LL-013: navigate targets are validated internal paths only.
   * Enforces TO-LL-036: never silently swallow errors; degrade gracefully to /sessions.
   */
  const resolveAndResumeActiveSession = async (): Promise<void> => {
    try {
      // Step 1 (TO-LL-026): Invalidate then force-refetch so cache is fresh.
      await queryClient.invalidateQueries({ queryKey: ['sessions'] });
      await queryClient.refetchQueries({ queryKey: ['sessions'] });

      // Step 2: Read the refreshed cache.
      let sessions = queryClient.getQueryData<{ sessions?: import('@/types/session').Session[] } | import('@/types/session').Session[]>(['sessions']);

      // Step 3: Normalise — getSessions returns Session[], but queryData may have been set
      // from the useSessions queryFn which returns Session[] directly.
      let sessionList: import('@/types/session').Session[] = [];
      if (Array.isArray(sessions)) {
        sessionList = sessions;
      } else if (sessions && typeof sessions === 'object' && 'sessions' in sessions && Array.isArray(sessions.sessions)) {
        sessionList = sessions.sessions;
      } else {
        // Cache was empty after refetch — try a direct service call as fallback.
        try {
          sessionList = await getSessions();
        } catch {
          sessionList = [];
        }
      }

      // Step 4: Find the first active session (status === 'active').
      const active = sessionList.find((s) => s.status === 'active');

      // Step 5 (TO-LL-013): Only navigate to a validated internal path — never an
      // undefined or server-supplied free-form URL.
      if (active?.sessionId) {
        navigate(`/session/${active.sessionId}`);
      } else {
        // Step 6 (TO-LL-026): Active session not yet visible (eventual consistency lag).
        // Navigate to /sessions which will poll and show the ResumeSessionHero.
        console.warn('[useStartSession] resolveAndResumeActiveSession: no active session found after refetch; falling back to /sessions');
        navigate('/sessions');
      }
    } catch (resolveError) {
      // Step 7 (TO-LL-036): Surface the warning but degrade gracefully — never navigate
      // to an undefined session id, never silently swallow the failure.
      console.warn('[useStartSession] resolveAndResumeActiveSession: refetch/resolve threw, falling back to /sessions', resolveError);
      navigate('/sessions');
    }
  };

  const mutation = useMutation<ServerlessSessionResponse, AuthError, string | undefined>({
    mutationFn: (preferredTopic) => startServerlessSession(preferredTopic),
    onSuccess: (data) => {
      // Navigate to the new session chat page
      navigate(`/session/${data.sessionId}`);
    },
    onError: async (error) => {
      // US-021 T2: FIRST branch — 402 INSUFFICIENT_COINS (family wallet empty).
      // Implements CCM-03: set isOutOfCoins=true and write the sticky React Query flag so
      // SessionsListPage and StudentDashboardPage both render the "0 sessions remaining"
      // state without re-attempting. Return early so conflict-redirect and getErrorMessage are
      // NOT reached — we never want 'Top up to start a new session' shown to a student.
      // Enforces TO-LL-036: ONLY this error code is suppressed; every other code continues
      // to flow through the conflict-redirect branch or getErrorMessage below (not swallowed).
      // Enforces TO-LL-013: no navigation is triggered here — there is no purchase/parent
      // surface to send the student to; the kid-friendly component directs them offline.
      if (error.code === AUTH_ERROR_CODES.INSUFFICIENT_COINS) {
        setIsOutOfCoins(true);
        // Sticky flag: any page reading useQuery(['family','outOfCoins']) will see true
        // for the rest of this app session without needing another start attempt.
        queryClient.setQueryData(['family', 'outOfCoins'], true);
        return;
      }

      // US-015: If the error is about active session limit, seamlessly redirect to the
      // existing active session instead of surfacing a generic error string (AC-1).
      // TO-LL-026: resolveAndResumeActiveSession() refetches before reading the cache.
      // BUG-20260701-230408 (RC-003): SESSION_ALREADY_ACTIVE added - it is the 422 code
      // start_session.py's commercial one-active-per-child guard emits; without it here,
      // the seamless-resume net never fired for that code even though it is a real
      // active-session conflict identical in kind to ACTIVE_SESSION_EXISTS - 2026-07-02
      if (
        error.code === 'ACTIVE_SESSION_EXISTS' ||
        error.code === 'SESSION_ALREADY_ACTIVE' ||
        error.code === 'SESSION_LIMIT_REACHED' ||
        error.code === 'CONFLICT' ||
        error.code === 'RATE_LIMITED'
      ) {
        // Mark as conflict redirect so the error getter returns null (no UI error shown).
        isConflictRedirectRef.current = true;
        await resolveAndResumeActiveSession();
        return;
      }
      // Non-conflict errors: do nothing here — error string is surfaced by the return
      // value via getErrorMessage(mutation.error) below. (TO-LL-036: not swallowed.)
      // Reset the conflict flag so any prior conflict state is cleared.
      isConflictRedirectRef.current = false;
    },
  });

  const startSession = (preferredTopic?: string) => {
    // Reset conflict flag on each new attempt so a subsequent non-conflict error shows.
    isConflictRedirectRef.current = false;
    // US-021: Reset isOutOfCoins on each new attempt so a retry can re-evaluate
    // (wallet may have been topped up in another device/session).
    setIsOutOfCoins(false);
    mutation.mutate(preferredTopic);
  };

  return {
    startSession,
    isStarting: mutation.isPending,
    // US-015: Suppress error string for the conflict family — the redirect IS the response.
    // US-021: Also suppress error string when isOutOfCoins is true (CCM-03: never show
    // the raw 'Top up to start a new session' backend message to a student; the
    // neutral "0 sessions remaining" state carries the kid-friendly copy instead).
    // Non-coin, non-conflict errors still flow through getErrorMessage (TO-LL-036: not swallowed).
    error:
      mutation.error && !isConflictRedirectRef.current && !isOutOfCoins
        ? getErrorMessage(mutation.error)
        : null,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
    // US-021: Exposed so pages can render the "0 sessions remaining" state and hide Start (AC-1).
    isOutOfCoins,
  };
}
